/**
 * Recorded sound effects: which files exist, and a bank that decodes them and hands one out.
 *
 * `architecture/AUDIO-VISUAL-EFFECTS.md` §3 and "Recorded samples" in the as-built half are the
 * contract. **A cue with a recording plays a recording; a cue without one plays its synthesised
 * sound, exactly as before** — a missing file, a failed decode or a bank still loading can never
 * make the game quieter than it was.
 *
 * Files are `assets/sfx/<sample>_<NN>.m4a`: `<sample>` is `Cue.sample`, and every `_NN` is one
 * take. Made by `grind/scripts/process-sfx.py` (mono, 48 kHz, trimmed to the attack, −15 dBFS RMS,
 * peaks under −1 dBFS after encoding).
 */

/** Every recorded take, by URL, keyed by the file's path. Resolved by Vite at build time. */
const FILES = import.meta.glob('../assets/sfx/*.m4a', { eager: true, query: '?url', import: 'default' }) as Record<
  string,
  string
>;

/**
 * The takes of each sample, by sample name: `impact_01.m4a` and `impact_02.m4a` are two takes of
 * `impact`. Sorted, so take order is stable.
 */
export function sampleIndex(files: Readonly<Record<string, string>> = FILES): Map<string, string[]> {
  const index = new Map<string, string[]>();
  for (const [path, url] of Object.entries(files).sort(([a], [b]) => a.localeCompare(b))) {
    const match = /\/([a-z_]+?)_(\d{2})\.m4a$/.exec(path);
    if (!match) continue;
    const takes = index.get(match[1]!) ?? [];
    takes.push(url);
    index.set(match[1]!, takes);
  }
  return index;
}

/** The subset of an `AudioContext` decoding needs, so a test can hand in a fake. */
export interface Decoder {
  decodeAudioData(data: ArrayBuffer): Promise<AudioBuffer>;
}

/**
 * Decoded takes, and the choice of which one plays.
 *
 * **Never the same take twice in a row** (§3's round-robin): with three takes, picking uniformly
 * repeats a third of the time, and a repeated hit is exactly the fatigue the takes exist to stop.
 */
export class SampleBank {
  private readonly buffers = new Map<string, AudioBuffer[]>();
  private readonly last = new Map<string, number>();

  constructor(
    private readonly index: ReadonlyMap<string, readonly string[]> = sampleIndex(),
    private readonly random: () => number = Math.random,
  ) {}

  /** The sample names with at least one take on disk — loaded or not. */
  get names(): string[] {
    return [...this.index.keys()];
  }

  /**
   * Fetches and decodes every take. **Fire and forget**: until a sample lands, its cue plays the
   * synthesised sound. A take that fails to fetch or decode is logged and skipped; its siblings
   * still play.
   */
  async load(decoder: Decoder, fetcher: (url: string) => Promise<ArrayBuffer> = fetchBytes): Promise<void> {
    await Promise.all(
      [...this.index].map(async ([name, urls]) => {
        const takes = await Promise.all(
          urls.map(async (url) => {
            try {
              return await decoder.decodeAudioData(await fetcher(url));
            } catch (error) {
              console.warn(`grind: sound ${url} failed to load, keeping the synthesised ${name}`, error);
              return null;
            }
          }),
        );
        const good = takes.filter((b): b is AudioBuffer => b !== null);
        if (good.length > 0) this.buffers.set(name, good);
      }),
    );
  }

  /** A take of `name`, not the one that played last, or null when there is none loaded. */
  pick(name: string): AudioBuffer | null {
    const takes = this.buffers.get(name);
    if (!takes || takes.length === 0) return null;
    if (takes.length === 1) return takes[0]!;
    const previous = this.last.get(name);
    let i: number;
    if (previous === undefined) {
      i = Math.floor(this.random() * takes.length);
    } else {
      // Uniform over the other n−1, then step past the previous one.
      i = Math.floor(this.random() * (takes.length - 1));
      if (i >= previous) i += 1;
    }
    this.last.set(name, i);
    return takes[i]!;
  }

  /** Whether any take of `name` has decoded. */
  has(name: string): boolean {
    return (this.buffers.get(name)?.length ?? 0) > 0;
  }
}

async function fetchBytes(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  return response.arrayBuffer();
}
